import React, { useState, useEffect, useCallback } from 'react';
import './TemporaryOrderViewer.css';
import { TemporaryOrderService, Utils } from '../../services/privateCatalogService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faReceipt,
    faCalendar,
    faUser,
    faPhone,
    faMapMarkerAlt,
    faStore,
    faShoppingBag,
    faTimes,
    faCheck
} from '@fortawesome/free-solid-svg-icons';

const API_BASE_URL = 'https://api.minymol.com';

const TemporaryOrderViewer = ({ token }) => {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [providerInfo, setProviderInfo] = useState(null);
    const [loadingProvider, setLoadingProvider] = useState(false);

    // Modal de notas (solo visible para el proveedor)
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [notesInput, setNotesInput] = useState('');
    const [savingNotes, setSavingNotes] = useState(false);
    const [notesSaved, setNotesSaved] = useState(false);

    const fetchProviderInfo = useCallback(async (providerId) => {
        try {
            setLoadingProvider(true);
            const response = await fetch(`https://api.minymol.com/providers/public/${providerId}`);
            
            if (response.ok) {
                const providerData = await response.json();
                setProviderInfo(providerData);
            } else {
                console.error('Error al obtener información del proveedor:', response.status);
            }
        } catch (error) {
            console.error('Error al obtener información del proveedor:', error);
        } finally {
            setLoadingProvider(false);
        }
    }, []);

    const fetchTemporaryOrder = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const data = await TemporaryOrderService.getTemporaryOrder(token);
            setOrder(data);
            
            // Obtener información del proveedor una vez que tenemos la orden
            if (data && data.providerId) {
                fetchProviderInfo(data.providerId);
            }
        } catch (error) {
            console.error('Error al obtener pedido temporal:', error);
            if (error.message === 'Orden temporal no encontrada') {
                setError('Pedido no encontrado. El enlace puede haber expirado.');
            } else {
                setError('Error al cargar el pedido. Por favor, intenta de nuevo.');
            }
        } finally {
            setLoading(false);
        }
    }, [token, fetchProviderInfo]);

    useEffect(() => {
        if (token) {
            fetchTemporaryOrder();
        }
    }, [token, fetchTemporaryOrder]);

    const formatDate = (dateString) => {
        return Utils.formatDate(dateString);
    };

    const openNotesModal = () => {
        setNotesInput(order.notes || '');
        setNotesSaved(false);
        setShowNotesModal(true);
    };

    const saveNotes = async () => {
        try {
            setSavingNotes(true);
            await fetch(`${API_BASE_URL}/temporary-orders/${token}/notes`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notes: notesInput }),
            });
            setOrder(prev => ({ ...prev, notes: notesInput }));
            setNotesSaved(true);
            setTimeout(() => setShowNotesModal(false), 800);
        } catch (err) {
            console.error('Error al guardar notas:', err);
        } finally {
            setSavingNotes(false);
        }
    };

    // Función auxiliar para separar valores concatenados con punto y coma
    const splitSpecValues = (value) => {
        if (!value || value === '') return [];
        return value.toString().split(';').filter(v => v && v.trim() !== '');
    };

    if (loading) {
        return (
            <div className="private-catalog-temp-order-loading">
                <div className="private-catalog-loading-spinner"></div>
                <p>Cargando pedido...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="private-catalog-temp-order-error">
                <FontAwesomeIcon icon={faReceipt} />
                <h2>Error</h2>
                <p>{error}</p>
                <button onClick={() => window.history.back()} className="private-catalog-back-btn">
                    Volver
                </button>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="private-catalog-temp-order-error">
                <FontAwesomeIcon icon={faReceipt} />
                <h2>Pedido no encontrado</h2>
                <p>No se pudo cargar la información del pedido.</p>
            </div>
        );
    }

    return (
        <div className="private-catalog-temp-order-viewer">
            <div className="private-catalog-order-container">
                {/* Header del Pedido */}
                <div className="private-catalog-order-header">
                    <div className="private-catalog-order-title">
                        <FontAwesomeIcon icon={faReceipt} />
                        <h1>Pedido</h1>
                    </div>
                    <div className="private-catalog-order-date">
                        <FontAwesomeIcon icon={faCalendar} />
                        <span>{formatDate(order.createdAt)}</span>
                    </div>
                </div>

                {/* Información del Cliente */}
                {(order.customerName || order.customerPhone || order.customerEmail || order.customerAddress) && (
                    <div className="private-catalog-customer-info private-catalog-section">
                        <h2>
                            <FontAwesomeIcon icon={faUser} />
                            Información del Cliente
                        </h2>
                        <div className="private-catalog-info-grid">
                            {order.customerName && (
                                <div className="private-catalog-info-item">
                                    <FontAwesomeIcon icon={faUser} />
                                    <span className="private-catalog-label">Nombre:</span>
                                    <span className="private-catalog-value">{order.customerName}</span>
                                </div>
                            )}
                            {order.customerPhone && (
                                <div className="private-catalog-info-item">
                                    <FontAwesomeIcon icon={faPhone} />
                                    <span className="private-catalog-label">Teléfono:</span>
                                    <span className="private-catalog-value">{order.customerPhone}</span>
                                </div>
                            )}
                            {order.customerEmail && (
                                <div className="private-catalog-info-item">
                                    <FontAwesomeIcon icon={faUser} />
                                    <span className="private-catalog-label">Email:</span>
                                    <span className="private-catalog-value">{order.customerEmail}</span>
                                </div>
                            )}
                            {order.customerAddress && (
                                <div className="private-catalog-info-item">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                                    <span className="private-catalog-label">Dirección:</span>
                                    <span className="private-catalog-value">{order.customerAddress}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Información del Proveedor */}
                <div className="private-catalog-provider-info private-catalog-section">
                    <h2>
                        <FontAwesomeIcon
                            icon={faStore}
                            onClick={openNotesModal}
                            style={{ cursor: 'pointer' }}
                        />
                        Proveedor
                    </h2>
                    {loadingProvider ? (
                        <div className="private-catalog-provider-loading">
                            <p>Cargando información del proveedor...</p>
                        </div>
                    ) : providerInfo ? (
                        <div className="private-catalog-provider-details">
                            <div className="private-catalog-provider-header">
                                {providerInfo.logo_url && (
                                    <img 
                                        src={providerInfo.logo_url} 
                                        alt={`Logo de ${providerInfo.nombre_empresa}`}
                                        className="private-catalog-provider-logo"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                )}
                                <div className="private-catalog-provider-info-text">
                                    <h3>{providerInfo.nombre_empresa}</h3>
                                    {providerInfo.descripcion && (
                                        <p className="private-catalog-provider-description">{providerInfo.descripcion}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="private-catalog-info-item">
                            <span className="private-catalog-label">ID del Proveedor:</span>
                            <span className="private-catalog-value">#{order.providerId}</span>
                        </div>
                    )}
                </div>

                {/* Lista de Productos */}
                <div className="private-catalog-order-items private-catalog-section">
                    <h2>
                        <FontAwesomeIcon icon={faShoppingBag} />
                        Productos ({order.items.reduce((total, item) => {
                            const colors = splitSpecValues(item.colorName);
                            const sizes = splitSpecValues(item.sizeName);
                            const quantities = splitSpecValues(item.quantity);
                            const maxVariants = Math.max(colors.length, sizes.length, quantities.length, 1);
                            return total + maxVariants;
                        }, 0)})
                    </h2>
                    <div className="private-catalog-items-list">
                        {order.items.map((item, index) => {
                            const colors = splitSpecValues(item.colorName);
                            const sizes = splitSpecValues(item.sizeName);
                            const quantities = splitSpecValues(item.quantity);
                            const hasMultipleVariants = colors.length > 1 || sizes.length > 1 || quantities.length > 1;

                            if (hasMultipleVariants) {
                                // Mostrar producto con múltiples variantes
                                const maxLength = Math.max(colors.length, sizes.length, quantities.length);
                                
                                return (
                                    <div key={index} className="private-catalog-order-item private-catalog-multiple-variants">
                                        <div className="private-catalog-item-image">
                                            {item.productImageUrl ? (
                                                <img src={item.productImageUrl} alt={item.productName} />
                                            ) : (
                                                <div className="private-catalog-no-image">
                                                    <FontAwesomeIcon icon={faShoppingBag} />
                                                </div>
                                            )}
                                        </div>

                                        <div className="private-catalog-item-details">
                                            <h3>{item.productName}</h3>
                                            
                                            {/* Múltiples variantes */}
                                            <div className="private-catalog-variants-container">
                                                {Array.from({ length: maxLength }, (_, i) => {
                                                    const color = colors[i] || '';
                                                    const size = sizes[i] || '';
                                                    const quantity = quantities[i] || '1';
                                                    
                                                    return (
                                                        <div key={i} className="private-catalog-variant-item">
                                                            <div className="private-catalog-variant-specs">
                                                                {color && color.trim() !== '' && (
                                                                    <div className="private-catalog-spec">
                                                                        <span className="private-catalog-spec-label">Color:</span>
                                                                        <span className="private-catalog-spec-value">{color}</span>
                                                                    </div>
                                                                )}
                                                                {size && size.trim() !== '' && (
                                                                    <div className="private-catalog-spec">
                                                                        <span className="private-catalog-spec-label">Talla:</span>
                                                                        <span className="private-catalog-spec-value">{size}</span>
                                                                    </div>
                                                                )}
                                                                <div className="private-catalog-spec">
                                                                    <span className="private-catalog-spec-label">Cantidad:</span>
                                                                    <span className="private-catalog-spec-value">{quantity}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {(item.unitPrice > 0 || item.totalPrice > 0) ? (
                                                <div className="private-catalog-item-pricing">
                                                    <span className="private-catalog-unit-price">${Math.round(item.unitPrice).toLocaleString('es-CO')} c/u</span>
                                                    <span className="private-catalog-total-price">${Math.round(item.totalPrice).toLocaleString('es-CO')}</span>
                                                </div>
                                            ) : (
                                                <div className="private-catalog-item-pricing-pending">
                                                    <span className="private-catalog-pricing-message">
                                                        💰 Precios por confirmar - Consulta con el proveedor
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            } else {
                                // Mostrar producto con una sola variante (comportamiento original)
                                return (
                                    <div key={index} className="private-catalog-order-item">
                                        <div className="private-catalog-item-image">
                                            {item.productImageUrl ? (
                                                <img src={item.productImageUrl} alt={item.productName} />
                                            ) : (
                                                <div className="private-catalog-no-image">
                                                    <FontAwesomeIcon icon={faShoppingBag} />
                                                </div>
                                            )}
                                        </div>

                                        <div className="private-catalog-item-details">
                                            <h3>{item.productName}</h3>
                                            <div className="private-catalog-item-specs">
                                                {item.colorName && item.colorName.trim() !== '' && (
                                                    <div className="private-catalog-spec">
                                                        <span className="private-catalog-spec-label">Color:</span>
                                                        <span className="private-catalog-spec-value">
                                                            {item.colorName}
                                                        </span>
                                                    </div>
                                                )}
                                                {item.sizeName && item.sizeName.trim() !== '' && (
                                                    <div className="private-catalog-spec">
                                                        <span className="private-catalog-spec-label">Talla:</span>
                                                        <span className="private-catalog-spec-value">
                                                            {item.sizeName}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="private-catalog-spec">
                                                    <span className="private-catalog-spec-label">Cantidad:</span>
                                                    <span className="private-catalog-spec-value">
                                                        {item.quantity || 1}
                                                    </span>
                                                </div>
                                            </div>
                                            {(item.unitPrice > 0 || item.totalPrice > 0) ? (
                                                <div className="private-catalog-item-pricing">
                                                    <span className="private-catalog-unit-price">${Math.round(item.unitPrice).toLocaleString('es-CO')} c/u</span>
                                                    <span className="private-catalog-total-price">${Math.round(item.totalPrice).toLocaleString('es-CO')}</span>
                                                </div>
                                            ) : (
                                                <div className="private-catalog-item-pricing-pending">
                                                    <span className="private-catalog-pricing-message">
                                                        💰 Precios por confirmar - Consulta con el proveedor
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            }
                        })}
                    </div>
                </div>

                {/* Total del Pedido */}
                {order.totalAmount > 0 ? (
                    <div className="private-catalog-order-total private-catalog-section">
                        <div className="private-catalog-total-row">
                            <span className="private-catalog-total-label">Total del Pedido:</span>
                            <span className="private-catalog-total-amount">${Math.round(order.totalAmount).toLocaleString('es-CO')}</span>
                        </div>
                    </div>
                ) : (
                    <div className="private-catalog-order-total-pending private-catalog-section">
                        <div className="private-catalog-total-pending-message">
                            <span className="private-catalog-total-pending-icon">💰</span>
                            <div className="private-catalog-total-pending-text">
                                <h3>Total por confirmar</h3>
                                <p>Los precios y el total final serán confirmados por el proveedor</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Información Adicional */}
                <div className="private-catalog-order-footer">
                    <p className="private-catalog-disclaimer">
                        ⚠️ Este es un pedido temporal para facilitar la comunicación.
                        No constituye una orden de compra confirmada.
                    </p>
                    <p className="private-catalog-powered-by">
                        Generado por OviCatálogos · Una solución de Minymol SAS
                    </p>
                </div>

                {/* Modal de notas internas */}
                {showNotesModal && (
                    <div
                        style={{
                            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 9999, padding: '1rem',
                        }}
                        onClick={() => setShowNotesModal(false)}
                    >
                        <div
                            style={{
                                background: '#fff', borderRadius: '12px', padding: '1.5rem',
                                width: '100%', maxWidth: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                            }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h3 style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                                    <FontAwesomeIcon icon={faStore} style={{ marginRight: '0.5rem', color: '#fa7e17' }} />
                                    Notas internas
                                </h3>
                                <button
                                    onClick={() => setShowNotesModal(false)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '1.1rem' }}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                            <textarea
                                value={notesInput}
                                onChange={e => setNotesInput(e.target.value)}
                                placeholder="Escribe tus notas sobre este pedido..."
                                rows={5}
                                style={{
                                    width: '100%', border: '1px solid #ddd', borderRadius: '8px',
                                    padding: '0.75rem', fontSize: '0.9rem', resize: 'vertical',
                                    fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
                                }}
                            />
                            <button
                                onClick={saveNotes}
                                disabled={savingNotes}
                                style={{
                                    marginTop: '1rem', width: '100%', padding: '0.75rem',
                                    backgroundColor: notesSaved ? '#28a745' : '#fa7e17',
                                    color: '#fff', border: 'none', borderRadius: '8px',
                                    fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer',
                                    transition: 'background 0.2s',
                                }}
                            >
                                {notesSaved
                                    ? <><FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.5rem' }} />Guardado</>
                                    : savingNotes ? 'Guardando...' : 'Guardar notas'
                                }
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemporaryOrderViewer;